import type {ReactNode} from 'react';
import {useMedia} from '@shopify/react-hooks';
import {InlineStack, BlockStack, Divider} from '@shopify/polaris';

import type {FilterInterface, AppliedFilterInterface} from './components';
import {Filters} from './components';
import styles from './FilterSectionLayout.scss';

interface BaseFilteringSectionProps {
  /** Available filters added to the sheet. Shortcut filters are exposed outside of the sheet. */
  filters: FilterInterface[];

  /** Applied filters which are rendered as tags. The remove callback is called with the respective key */
  appliedFilters: AppliedFilterInterface[];

  /** Callback when the filters are opened */
  onFiltersOpen?(): void;

  /** Callback when the filters are closed */
  onFiltersClose?(): void;

  /** Callback when clearing all filters */
  onClearAllFilters?(): void;

  /** Disable all filters */
  disabled?: boolean;

  /** Additional hint text to display below the filters */
  helpText?: string | ReactNode;

  /** Hide tags for applied filters */
  hideTags?: boolean;
}

interface FilteringSectionPropsWithQuery {
  /** Hide the query field */
  hideQueryField?: false;

  /** Currently entered text in the query field */
  queryValue?: string;

  /** Placeholder text for the query field */
  queryPlaceholder?: string;

  /** Whether the query field is focused */
  focused?: boolean;

  /** Callback when the query field is changed */
  onQueryChange(queryValue: string): void;

  /** Callback when the clear button is triggered */
  onQueryClear(): void;

  /** Callback when the query field is blurred */
  onQueryBlur?(): void;

  /** Callback when the query field is focused */
  onQueryFocus?(): void;

  /** Whether to always show the clear button in the query field */
  alwaysVisibleClearButton?: boolean;
}

interface FilteringSectionPropsWithoutQuery
  extends Partial<Omit<FilteringSectionPropsWithQuery, 'hideQueryField'>> {
  /** Hide the query field */
  hideQueryField: true;
}

export type FilteringSectionProps = BaseFilteringSectionProps &
  (FilteringSectionPropsWithQuery | FilteringSectionPropsWithoutQuery);

interface CommonProps {
  filterOptions: FilteringSectionProps;
}

interface DetailedProps {
  /** Whether to render a layout with the filter below the search box */
  detailedLayout: true;
  /** Additional content you want to show relating to the filter ex. count of results */
  filterDetails: ReactNode;
}

interface NonDetailedProps {
  detailedLayout?: false;
  filterDetails?: ReactNode;
}

type Props = CommonProps & (DetailedProps | NonDetailedProps);

export function FilterSectionLayout({
  filterOptions: {
    filters,
    hideTags,
    disabled,
    queryPlaceholder,
    onQueryChange,
    onQueryBlur,
    onQueryFocus,
    queryValue,
    focused,
    onQueryClear,
    appliedFilters,
    onFiltersOpen,
    onFiltersClose,
    alwaysVisibleClearButton,
    hideQueryField,
  },
  filterDetails,
  detailedLayout,
}: Props) {
  const isSmallScreen = useMedia('(max-width: 30.625em)');

  const filterMarkup = filters.length ? (
    <div className={styles.FiltersWrapper}>
      <Filters
        filters={filters}
        disabled={disabled}
        iconOnly={!isSmallScreen}
        onOpen={onFiltersOpen}
        onClose={onFiltersClose}
      />
    </div>
  ) : undefined;
  const searchMarkup = hideQueryField ? null : (
    <div className={styles.SearchWrapper}>
      <Filters.Search
        queryPlaceholder={queryPlaceholder}
        onQueryChange={onQueryChange!}
        onQueryBlur={onQueryBlur}
        onQueryFocus={onQueryFocus}
        queryValue={queryValue}
        focused={focused}
        onQueryClear={onQueryClear!}
        disabled={disabled}
        alwaysVisibleClearButton={alwaysVisibleClearButton}
      />
    </div>
  );
  const tagsMarkup = hideTags ? null : (
    <Filters.Tags
      appliedFilters={appliedFilters}
      disabled={disabled}
      onFiltersChange={() => {}}
    />
  );

  if (detailedLayout) {
    return (
      <BlockStack>
        <BlockStack gap="300">
          {searchMarkup && (
            <>
              {searchMarkup}
              <Divider />
            </>
          )}
          {filterDetails && (
            <>
              <InlineStack blockAlign="center" align="space-between">
                {filterDetails}
                {filterMarkup}
              </InlineStack>
            </>
          )}
        </BlockStack>
        {tagsMarkup}
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="200">
      <div className={styles.BetterOrderFilterControl}>
        {searchMarkup}
        {filterMarkup}
      </div>
      {filterDetails}
      {tagsMarkup}
    </BlockStack>
  );
}
